import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLoaderData } from 'react-router-dom';
import {
	CCard, CCardBody, CCol, CRow, CButton,
	CFormLabel, CFormInput, CBadge,
	CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody,
} from '@coreui/react';
import AxiosInstance from 'src/components/Axios';
import { STATUS_COLORS, STATUS_LABELS, SENDER_TYPE_COLORS, SENDER_TYPE_LABELS, formatDateTime } from 'src/constants/supportLetters';
import {
	setCachedRequest, getCachedRequest,
	getCachedSession, setCachedSession,
} from 'src/utils/supportRequestCache';
import { MediaViewerModal, MediaThumbGallery } from 'src/components/myComponents/MediaViewerModal';
import SupportLettersModal from '../SupportLettersModal';
import SupportAttachmentImportNotice from '../SupportAttachmentImportNotice';
import scrollToTop from 'src/components/store/scrollToTop';
import SupportRequestSessionThread from './SupportRequestSessionThread';
import SupportRequestRelevantQa from './SupportRequestRelevantQa';
import SupportRequestDrafts from './SupportRequestDrafts';
import SupportRequestHistoryEmail from './SupportRequestHistoryEmail';

const urlApiletters = '/support-letters';

export async function LoaderSupportLettersView({ params }) {
	return AxiosInstance.get(`/support-requests/${params.id}`);
}

const SupportLettersView = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const loaderData = useLoaderData();

	const data = loaderData?.data ?? null;

	const [session, setSession] = useState(null);
	const [historyPatchedRow, setHistoryPatchedRow] = useState(null);
	const [relevantQa, setRelevantQa] = useState([]);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalRequestId, setModalRequestId] = useState(null);
	const [viewerOpen, setViewerOpen] = useState(false);
	const [viewerIndex, setViewerIndex] = useState(0);

	// Зберігаємо в кеш після завантаження лоадером
	useEffect(() => {
		if (data) setCachedRequest(id, data);
	}, [data, id]);

	// Скрол наверх при переході на інший лист
	useEffect(() => {
		scrollToTop();
	}, [id]);

	// Завантаження вторинних даних
	useEffect(() => {
		if (!data) return;

		if (data.session_id) {
			const cachedSession = getCachedSession(data.session_id);
			if (cachedSession) {
				const patched = {
					...cachedSession,
					requests: (cachedSession.requests || []).map(r =>
						r.id === data.id ? { ...r, status: data.status } : r
					),
				};
				setSession(patched);
				setCachedSession(data.session_id, patched);
			} else {
				AxiosInstance.get(`/support-sessions/${data.session_id}`)
					.then(res => {
						setSession(res.data);
						setCachedSession(data.session_id, res.data);
					})
					.catch(() => { });
			}
		}

		if (data.user_email) {
			setHistoryPatchedRow({ id: data.id, status: data.status });
		}

		if (data.message) {
			AxiosInstance.get('/qa', { params: { search: data.message.slice(0, 100), page: 1, itemsPerPage: 5 } })
				.then(res => setRelevantQa(res.data.data ?? []))
				.catch(() => { });
		}
	}, [data]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleOpenModal = (requestId) => {
		setModalRequestId(requestId);
		setModalOpen(true);
	};

	const handleCloseModal = () => {
		const cachedReq = modalRequestId ? getCachedRequest(modalRequestId) : null;
		if (cachedReq) {
			setSession(prev => prev ? {
				...prev,
				requests: (prev.requests || []).map(r =>
					r.id === modalRequestId ? { ...r, status: cachedReq.status } : r
				),
			} : prev);
			setHistoryPatchedRow({ id: modalRequestId, status: cachedReq.status });
		}
		setModalOpen(false);
	};

	if (!data) {
		return (
			<CCard className="mb-4">
				<CCardBody>
					<p className="text-danger">Лист не знайдено.</p>
					<CButton color="secondary" variant="outline" onClick={() => navigate(-1)} title="Повернутись">
						← Назад
					</CButton>
				</CCardBody>
			</CCard>
		);
	}

	const buttonsBlock = (
		<div className="d-flex gap-2 mb-4">
			<CButton color="secondary" variant="outline" onClick={() => navigate(-1)} title="Повернутись до попередньої сторінки">
				← Повернутись
			</CButton>
			<CButton
				color="success"
				onClick={() => navigate(`${urlApiletters}/update/${id}`)}
				title="Перейти до редагування листа"
			>
				Редагувати
			</CButton>
			{data.session_id && (
				<CButton
					color="info"
					variant="outline"
					onClick={() => navigate(`${urlApiletters}/session/${data.session_id}`)}
					title="Відкрити сторінку сесії листування"
				>
					Відкрити сесію
				</CButton>
			)}
		</div>
	);

	return (
		<>
			<SupportLettersModal
				open={modalOpen}
				requestId={modalRequestId}
				onClose={handleCloseModal}
			/>
			<MediaViewerModal
				open={viewerOpen}
				onClose={() => setViewerOpen(false)}
				attachments={data.attachments || []}
				startIndex={viewerIndex}
			/>

			{/* ── Кнопки ── */}
			{buttonsBlock}

			{/* ── Основна інформація ── */}
			<CCard className="mb-4">
				<CAccordion activeItemKey={1}>
					<CAccordionItem itemKey={1}>
						<CAccordionHeader>
							<strong>Лист #{data.id} — {data?.user_name} {data?.user_email}</strong>
						</CAccordionHeader>
						<CAccordionBody className="p-3">
							<CRow className="g-3">
								<CCol md={4}>
									<CFormLabel>Email</CFormLabel>
									<CFormInput value={data.user_email || ''} disabled readOnly />
								</CCol>
								<CCol md={4}>
									<CFormLabel>Ім'я</CFormLabel>
									<CFormInput value={data.user_name || ''} disabled readOnly />
								</CCol>
								<CCol md={4}>
									<CFormLabel>Телефон</CFormLabel>
									<CFormInput value={data.user_phone || '—'} disabled readOnly />
								</CCol>
								<CCol md={4}>
									<CFormLabel>Тема</CFormLabel>
									<CFormInput
										value={data.subject || ''}
										title={data.subject || ''}
										disabled readOnly
										style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
									/>
								</CCol>
								<CCol md={4}>
									<CFormLabel>Статус</CFormLabel>
									<div className="pt-2">
										<CBadge color={STATUS_COLORS[data.status] || 'secondary'} style={{ fontSize: '0.9rem' }}>
											{STATUS_LABELS[data.status] || data.status || '—'}
										</CBadge>
									</div>
								</CCol>
								<CCol md={4}>
									<CFormLabel>Від</CFormLabel>
									<div className="pt-2">
										<CBadge color={SENDER_TYPE_COLORS[data.sender_type] || 'secondary'} style={{ fontSize: '0.9rem' }}>
											{SENDER_TYPE_LABELS[data.sender_type] || data.sender_type || '—'}
										</CBadge>
									</div>
								</CCol>
								<CCol md={4}>
									<CFormLabel>Канал</CFormLabel>
									<CFormInput value={data.channel?.name || '—'} disabled readOnly />
								</CCol>
								<CCol md={4}>
									<CFormLabel>Дата отримання</CFormLabel>
									<CFormInput value={formatDateTime(data.sent_at || data.created_at)} disabled readOnly />
								</CCol>
								<CCol md={4}>
									<CFormLabel>Дата внесення</CFormLabel>
									<CFormInput value={formatDateTime(data.created_at)} disabled readOnly />
								</CCol>
								<CCol md={4}>
									<CFormLabel>Останнє оновлення</CFormLabel>
									<CFormInput value={formatDateTime(data.updated_at)} disabled readOnly />
								</CCol>
								{data.replied_at && (
									<CCol md={4}>
										<CFormLabel>Дата відповіді</CFormLabel>
										<CFormInput value={formatDateTime(data.replied_at)} disabled readOnly />
									</CCol>
								)}
								<CCol md={4}>
									<CFormLabel>Сесія</CFormLabel>
									<CFormInput value={data.session_id ? `#${data.session_id}` : '—'} disabled readOnly />
								</CCol>
								{data.call_center_id && (
									<CCol md={4}>
										<CFormLabel>ID в кол-центрі</CFormLabel>
										<CFormInput value={data.call_center_id} disabled readOnly />
									</CCol>
								)}
								{data.account && (
									<CCol md={4}>
										<CFormLabel>Особовий рахунок</CFormLabel>
										<CFormInput value={data.account} disabled readOnly />
									</CCol>
								)}
							</CRow>

							{/* Перший прочитав */}
							{data.first_read_by && (
								<div className="mt-3 p-2 bg-light rounded">
									<small className="text-muted">
										Перший прочитав: <strong>{[
											data.first_read_by.email,
											data.first_read_by.username,
											data.first_read_by.first_name,
											data.first_read_by.last_name,
										].filter(Boolean).join(' ')}</strong>
										{data.first_read_at ? ` о ${formatDateTime(data.first_read_at)}` : ''}
									</small>
								</div>
							)}

							{/* Повне повідомлення */}
							{data.message && (
								<div className="mt-3">
									<CFormLabel>Повідомлення</CFormLabel>
									<div
										className="form-control"
										style={{ background: '#f8f9fa', minHeight: '80px', whiteSpace: 'pre-wrap', maxHeight: '300px', overflowY: 'auto' }}
									>
										{data.message}
									</div>
								</div>
							)}

							{/* Вкладення (read-only) */}
							{((data.attachments || []).length > 0 || (data.attachment_imports || []).length > 0) && (
								<div className="mt-3 border rounded p-2">
									<SupportAttachmentImportNotice
										imports={data.attachment_imports}
										requestId={data.id}
									/>
									<div className="d-flex justify-content-between align-items-center mb-2">
										<strong>Вкладення ({(data.attachments || []).length})</strong>
										<CButton
											color="secondary"
											variant="outline"
											onClick={() => { setViewerIndex(0); setViewerOpen(true); }}
											disabled={(data.attachments || []).length === 0}
											title="Переглянути вкладення"
										>
											Переглянути вкладення
										</CButton>
									</div>
									{(data.attachments || []).length > 0 && (
										<MediaThumbGallery
											attachments={data.attachments}
											onSelect={(idx) => { setViewerIndex(idx); setViewerOpen(true); }}
										/>
									)}
								</div>
							)}
						</CAccordionBody>
					</CAccordionItem>
				</CAccordion>
			</CCard>

			{/* ── Команди відповідальних (read-only) ── */}
			<CCard className="mb-4">
				<CAccordion activeItemKey={1}>
					<CAccordionItem itemKey={1}>
						<CAccordionHeader><strong>Команди відповідальних</strong></CAccordionHeader>
						<CAccordionBody className="p-3">
							{(data.responders || []).length === 0
								? <span className="text-muted">—</span>
								: (data.responders || []).map(r => (
									<CBadge key={r.team_id} color="info" className="me-2">
										{r.team_name} ({r.team_code})
									</CBadge>
								))
							}
						</CAccordionBody>
					</CAccordionItem>
				</CAccordion>
			</CCard>

			{/* ── Категорії сесії (read-only) ── */}
			{data.session_id && (
				<CCard className="mb-4">
					<CAccordion activeItemKey={1}>
						<CAccordionItem itemKey={1}>
							<CAccordionHeader><strong>Категорії сесії</strong></CAccordionHeader>
							<CAccordionBody className="p-3">
								{(session?.categories || []).length === 0
									? <span className="text-muted">—</span>
									: (session?.categories || []).map(c => (
										<CBadge key={c.id} color="secondary" className="me-2">{c.name}</CBadge>
									))
								}
							</CAccordionBody>
						</CAccordionItem>
					</CAccordion>
				</CCard>
			)}

			{/* ── Переписка в сесії (read-only) ── */}
			<SupportRequestSessionThread session={session} currentId={id} onOpenModal={handleOpenModal} />

			{/* Вимкнено: блок недороблений, найближчим часом не в роботі — 04.09.2026 */}
			{/* <SupportRequestRelevantQa relevantQa={relevantQa} /> */}

			{/* ── Чернетки (read-only) ── */}
			<SupportRequestDrafts drafts={data.drafts} />

			{/* ── Історія користувача ── */}
			<SupportRequestHistoryEmail
				userEmail={data.user_email}
				currentId={id}
				onOpenModal={handleOpenModal}
				patchedRow={historyPatchedRow}
			/>

			{/* ── Кнопки ── */}
			{buttonsBlock}
		</>
	);
};

export default SupportLettersView;
